import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Copy, Check, Wand2, Zap, FileText, X } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";

const PLATFORMS = [
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "x", label: "X" },
  { id: "tiktok", label: "TikTok" },
  { id: "youtube", label: "YouTube" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "whatsapp", label: "WhatsApp" },
];

const PODCAST_FIELDS = [
  { id: "title", label: "Titles" },
  { id: "description", label: "Description" },
  { id: "show_notes", label: "Show notes" },
  { id: "seo_keywords", label: "SEO keywords" },
  { id: "hashtags", label: "Hashtags" },
  { id: "summary", label: "Summary" },
];

function CopyBtn({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-1 text-xs text-ink-secondary hover:text-cyan-300 transition"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function IdeateBtn({ field, context, onText, testid, label = "AI suggest", disabled }) {
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ideate-input", { field, context });
      if (data?.text) {
        onText(data.text);
        toast.success("AI suggestion ready");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI suggestion failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={loading || disabled}
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-2 py-1 type-status-badge font-semibold text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition"
      title={disabled ? "Fill required context first" : "Ask AI for an idea"}
    >
      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
      {loading ? "Thinking…" : label}
    </button>
  );
}

function OutputBlock({ title, output, testid }) {
  return (
    <div className="rounded-lg border border-line bg-bg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">{title}</span>
        <CopyBtn text={output} testid={`${testid}-copy`} />
      </div>
      <pre className="font-sans type-body whitespace-pre-wrap text-ink-primary" data-testid={testid}>{output}</pre>
    </div>
  );
}

export default function Generate() {
  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="generate-page">
      <PageHeader
        eyebrow="// AI STUDIO"
        title="Generate broadcast-ready content."
        description="Claude Sonnet 4.5 crafts copy tuned to your platform, voice, and audience."
      />
      <ImportedFromLibraryBanner />

      <Tabs defaultValue="caption" className="w-full">
        <TabsList className="bg-bg-surface border border-line p-1 rounded-md mb-6" data-testid="generate-tabs">
          <TabsTrigger value="caption" data-testid="tab-caption" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black font-medium">Social caption</TabsTrigger>
          <TabsTrigger value="bio" data-testid="tab-bio" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black font-medium">Artist bio</TabsTrigger>
          <TabsTrigger value="podcast" data-testid="tab-podcast" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black font-medium">Podcast</TabsTrigger>
        </TabsList>

        <TabsContent value="caption"><CaptionTool /></TabsContent>
        <TabsContent value="bio"><BioTool /></TabsContent>
        <TabsContent value="podcast"><PodcastTool /></TabsContent>
      </Tabs>
    </div>
  );
}

// Surfaces content sent from the Library "Send to → AI Studio" action.
// Shows the imported snippet with one-click copy so the user can paste it
// into whichever tool they want to re-generate from.
function ImportedFromLibraryBanner() {
  const [imp, setImp] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("bfa:import_ai_studio");
      if (raw) setImp(JSON.parse(raw));
    } catch { /* no-op */ }
  }, []);
  if (!imp) return null;
  const dismiss = () => { sessionStorage.removeItem("bfa:import_ai_studio"); setImp(null); };
  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text || ""); toast.success(label); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="mb-6 rounded-xl border border-cyan-500/40 bg-cyan-500/5 p-4" data-testid="ai-studio-imported-banner">
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-lg bg-cyan-500/15 text-cyan-300 grid place-items-center shrink-0">
          <FileText className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300">// IMPORTED FROM CONTENT LIBRARY</span>
            <span className="rounded-full bg-cyan-500/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-cyan-200">
              {(imp.type || "content").replace(/_/g, " ")}
            </span>
          </div>
          <p className="text-xs text-ink-secondary mt-1 mb-2">
            Copy the original prompt or output below and paste it into the tool of your choice to remix or refine.
          </p>
          {imp.prompt && (
            <div className="rounded-lg border border-line bg-bg p-3 mb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Original prompt</span>
                <button onClick={() => copy(imp.prompt, "Prompt copied")} className="text-[11px] text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid="ai-studio-imported-copy-prompt">
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
              <div className="text-xs text-ink-secondary whitespace-pre-wrap line-clamp-3">{imp.prompt}</div>
            </div>
          )}
          <div className="rounded-lg border border-line bg-bg p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Original output</span>
              <button onClick={() => copy(imp.output, "Output copied")} className="text-[11px] text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid="ai-studio-imported-copy-output">
                <Copy className="h-3 w-3" /> Copy
              </button>
            </div>
            <div className="text-xs text-ink-primary whitespace-pre-wrap line-clamp-4">{imp.output}</div>
          </div>
        </div>
        <button onClick={dismiss} className="text-ink-muted hover:text-ink-primary transition" data-testid="ai-studio-imported-dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function CaptionTool() {
  const [topic, setTopic] = useState("");
  const [showName, setShowName] = useState("");
  const [djName, setDjName] = useState("");
  const [tone, setTone] = useState("energetic");
  const [selected, setSelected] = useState(["instagram", "facebook", "x"]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const toggle = (p) => setSelected((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));

  const run = async () => {
    if (!topic.trim()) { toast.error("Tell us the topic"); return; }
    if (selected.length === 0) { toast.error("Pick at least one platform"); return; }
    setLoading(true);
    setItems([]);
    try {
      const res = await api.post("/ai/generate-caption", {
        topic,
        platforms: selected,
        show_name: showName,
        dj_name: djName,
        tone,
      });
      setItems(res.data.items);
      toast.success(`${res.data.items.length} captions ready`);
    } catch (e) {
      toast.error("Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const [autoBusy, setAutoBusy] = useState(false);
  const autoCompose = async () => {
    setAutoBusy(true);
    setItems([]);
    try {
      const { data } = await api.post("/ai/auto-compose", {
        mode: "caption",
        context: { show_name: showName, dj_name: djName },
      });
      setTopic(data.topic);
      setTone(data.tone);
      setSelected(data.platforms);
      toast.success(`Topic + ${data.platforms.length} platforms picked — generating…`);
      // Kick off the actual generation with the freshly-set values
      setLoading(true);
      try {
        const res = await api.post("/ai/generate-caption", {
          topic: data.topic,
          platforms: data.platforms,
          show_name: showName,
          dj_name: djName,
          tone: data.tone,
        });
        setItems(res.data.items);
        toast.success(`${res.data.items.length} captions ready`);
      } finally {
        setLoading(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-compose failed");
    } finally {
      setAutoBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-4" data-testid="caption-form">
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Topic *</Label>
            <IdeateBtn
              field="caption_topic"
              context={{ show_name: showName, dj_name: djName, tone }}
              onText={(t) => setTopic(t)}
              testid="caption-ideate-btn"
            />
          </div>
          <Textarea
            data-testid="caption-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="E.g. Tonight's late-night house mix featuring guest DJ Nova"
            className="mt-1 bg-bg border border-line"
            rows={3}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Show name</Label>
            <Input data-testid="caption-show" value={showName} onChange={(e) => setShowName(e.target.value)} className="mt-1 bg-bg border border-line" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">DJ name</Label>
            <Input data-testid="caption-dj" value={djName} onChange={(e) => setDjName(e.target.value)} className="mt-1 bg-bg border border-line" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Tone</Label>
          <Select value={tone} onValueChange={setTone}>
            <SelectTrigger className="mt-1 bg-bg border border-line" data-testid="caption-tone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-bg-elevated border-line">
              {["energetic", "professional", "playful", "moody", "hype"].map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary mb-2 block">Platforms</Label>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((p) => (
              <button
                key={p.id}
                onClick={() => toggle(p.id)}
                data-testid={`platform-toggle-${p.id}`}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  selected.includes(p.id)
                    ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
                    : "border-line bg-bg-elevated text-ink-secondary hover:text-ink-primary"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={run}
          disabled={loading}
          data-testid="caption-generate-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-5 py-3 type-button-label text-black amber-glow disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? "Generating..." : "Generate captions"}
        </button>
        <button
          onClick={autoCompose}
          disabled={autoBusy || loading}
          data-testid="caption-auto-compose-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-5 py-2.5 type-button-label text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition"
        >
          {autoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
          {autoBusy ? "Composing…" : "Compose for me"}
        </button>
      </div>

      <div className="space-y-3" data-testid="caption-output">
        {loading && (
          <div className="rounded-xl border border-brand-amber/40 bg-bg-surface p-8 text-center">
            <div className="font-mono text-xs text-brand-amber animate-pulse">// CLAUDE 4.5 STREAMING...</div>
            <div className="mt-3 text-ink-secondary text-sm">Crafting copy for {selected.length} platforms</div>
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center text-ink-muted text-sm">
            Your generated captions will appear here.
          </div>
        )}
        {items.map((it) => (
          <OutputBlock key={it.content_id} title={it.platform} output={it.output} testid={`caption-result-${it.platform}`} />
        ))}
      </div>
    </div>
  );
}

function BioTool() {
  const [form, setForm] = useState({ artist_name: "", genre: "", notable_works: "", style: "engaging", length: "medium" });
  const [loading, setLoading] = useState(false);
  const [streamed, setStreamed] = useState("");
  const [contentId, setContentId] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);

  const runWith = async (payload) => {
    setLoading(true);
    setStreamed("");
    setContentId(null);
    try {
      const res = await fetch(`${API_BASE}/ai/stream/bio`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const block of parts) {
          const lines = block.split("\n");
          let eventName = "message";
          let dataStr = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) eventName = ln.slice(6).trim();
            else if (ln.startsWith("data:")) dataStr += ln.slice(5).trim();
          }
          if (!dataStr) continue;
          try {
            const parsed = JSON.parse(dataStr);
            if (eventName === "done") setContentId(parsed.content_id);
            else if (eventName === "error") toast.error(parsed);
            else if (typeof parsed === "string") setStreamed((s) => s + parsed);
          } catch { /* skip malformed SSE chunk */ }
        }
      }
      toast.success("Bio generated");
    } catch (e) {
      toast.error("Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const run = async () => {
    if (!form.artist_name.trim()) { toast.error("Artist name required"); return; }
    await runWith(form);
  };

  const autoCompose = async () => {
    if (!form.artist_name.trim()) { toast.error("Artist name required first"); return; }
    setAutoBusy(true);
    try {
      const { data } = await api.post("/ai/auto-compose", {
        mode: "bio",
        context: { artist_name: form.artist_name, genre: form.genre },
      });
      const next = {
        ...form,
        genre: data.genre || form.genre,
        notable_works: data.notable_works || form.notable_works,
        style: data.style || form.style,
        length: data.length || form.length,
      };
      setForm(next);
      toast.success("Dossier prepared — streaming bio now…");
      await runWith(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-compose failed");
    } finally {
      setAutoBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-4" data-testid="bio-form">
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Artist name *</Label>
          <Input data-testid="bio-artist" value={form.artist_name} onChange={(e) => setForm({ ...form, artist_name: e.target.value })} className="mt-1 bg-bg border border-line" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Genre</Label>
          <Input data-testid="bio-genre" value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} className="mt-1 bg-bg border border-line" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Notable works / context</Label>
            <IdeateBtn
              field="bio_works"
              context={{ artist_name: form.artist_name, genre: form.genre }}
              onText={(t) => setForm((f) => ({ ...f, notable_works: t }))}
              testid="bio-ideate-btn"
              disabled={!form.artist_name.trim()}
            />
          </div>
          <Textarea data-testid="bio-context" value={form.notable_works} onChange={(e) => setForm({ ...form, notable_works: e.target.value })} className="mt-1 bg-bg border border-line" rows={3} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Style</Label>
            <Select value={form.style} onValueChange={(v) => setForm({ ...form, style: v })}>
              <SelectTrigger className="mt-1 bg-bg border border-line" data-testid="bio-style"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-bg-elevated border-line">
                {["engaging", "professional", "punchy", "literary"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Length</Label>
            <Select value={form.length} onValueChange={(v) => setForm({ ...form, length: v })}>
              <SelectTrigger className="mt-1 bg-bg border border-line" data-testid="bio-length"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-bg-elevated border-line">
                {["short", "medium", "long"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <button
          onClick={run}
          disabled={loading}
          data-testid="bio-generate-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-5 py-3 type-button-label text-black amber-glow disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? "Streaming..." : "Generate bio (live stream)"}
        </button>
        <button
          onClick={autoCompose}
          disabled={autoBusy || loading || !form.artist_name.trim()}
          data-testid="bio-auto-compose-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-5 py-2.5 type-button-label text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition"
          title={!form.artist_name.trim() ? "Enter artist name first" : "AI fills dossier + streams the bio"}
        >
          {autoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
          {autoBusy ? "Composing…" : "Compose for me"}
        </button>
      </div>

      <div data-testid="bio-output">
        {!loading && !streamed && (
          <div className="rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center text-ink-muted text-sm">
            Your artist biography will stream here in real time.
          </div>
        )}
        {(loading || streamed) && (
          <div className="rounded-xl border border-brand-amber/40 bg-bg-surface p-5" data-testid="bio-stream">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">
                {loading ? "// CLAUDE 4.5 STREAMING..." : `// bio · ${form.artist_name}`}
              </span>
              {!loading && streamed && (
                <CopyBtn text={streamed} testid="bio-copy" />
              )}
            </div>
            <pre className={`font-sans text-sm whitespace-pre-wrap text-ink-primary leading-relaxed ${loading ? "caret" : ""}`}>
              {streamed}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function PodcastTool() {
  const [form, setForm] = useState({ topic: "", show_id: "", show_name: "", guests: "", duration_minutes: 30 });
  const [fields, setFields] = useState(PODCAST_FIELDS.map((f) => f.id));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [shows, setShows] = useState([]);

  React.useEffect(() => {
    api.get("/shows").then((r) => setShows(r.data)).catch(() => {});
  }, []);

  const toggle = (id) => setFields((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const run = async () => {
    if (!form.topic.trim()) { toast.error("Topic required"); return; }
    if (fields.length === 0) { toast.error("Pick at least one field"); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/ai/generate-podcast", { ...form, fields });
      setResult(res.data);
      toast.success("Podcast package generated");
    } catch { toast.error("Generation failed"); }
    finally { setLoading(false); }
  };

  const [autoBusy, setAutoBusy] = useState(false);
  const autoCompose = async () => {
    setAutoBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/auto-compose", {
        mode: "podcast",
        context: { show_name: form.show_name, guests: form.guests },
      });
      const next = {
        ...form,
        topic: data.topic || form.topic,
        duration_minutes: data.duration_minutes || form.duration_minutes,
      };
      setForm(next);
      setFields(data.fields);
      toast.success("Topic + fields picked — producing package…");
      setLoading(true);
      try {
        const res = await api.post("/ai/generate-podcast", { ...next, fields: data.fields });
        setResult(res.data);
        toast.success("Podcast package generated");
      } finally {
        setLoading(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-compose failed");
    } finally {
      setAutoBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-6 space-y-4" data-testid="podcast-form">
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Episode topic *</Label>
            <IdeateBtn
              field="podcast_topic"
              context={{ show_name: form.show_name, guests: form.guests, duration_minutes: form.duration_minutes }}
              onText={(t) => setForm((f) => ({ ...f, topic: t }))}
              testid="podcast-ideate-btn"
            />
          </div>
          <Textarea data-testid="podcast-topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="mt-1 bg-bg border border-line" rows={3} />
        </div>
        {shows.length > 0 && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Attach to show</Label>
            <Select value={form.show_id || "none"} onValueChange={(v) => {
              if (v === "none") setForm({ ...form, show_id: "", show_name: form.show_name });
              else {
                const sh = shows.find((s) => s.show_id === v);
                setForm({ ...form, show_id: v, show_name: sh?.name || form.show_name });
              }
            }}>
              <SelectTrigger className="mt-1 bg-bg border border-line" data-testid="podcast-show-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-bg-elevated border-line">
                <SelectItem value="none">— None —</SelectItem>
                {shows.map((s) => <SelectItem key={s.show_id} value={s.show_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="mt-1 text-[10px] font-mono text-ink-muted">Episodes attached to a show appear on its public Share page.</div>
          </div>
        )}
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Show name</Label>
          <Input data-testid="podcast-show" value={form.show_name} onChange={(e) => setForm({ ...form, show_name: e.target.value })} className="mt-1 bg-bg border border-line" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Guests</Label>
          <Input data-testid="podcast-guests" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="mt-1 bg-bg border border-line" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Duration (minutes)</Label>
          <Input
            data-testid="podcast-duration"
            type="number"
            value={form.duration_minutes}
            onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) || 0 })}
            className="mt-1 bg-bg border border-line"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary mb-2 block">Fields to generate</Label>
          <div className="flex flex-wrap gap-2">
            {PODCAST_FIELDS.map((f) => (
              <button
                key={f.id}
                onClick={() => toggle(f.id)}
                data-testid={`podcast-field-${f.id}`}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  fields.includes(f.id)
                    ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
                    : "border-line bg-bg-elevated text-ink-secondary"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={run}
          disabled={loading}
          data-testid="podcast-generate-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-5 py-3 type-button-label text-black amber-glow disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? "Producing..." : "Generate podcast package"}
        </button>
        <button
          onClick={autoCompose}
          disabled={autoBusy || loading}
          data-testid="podcast-auto-compose-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-5 py-2.5 type-button-label text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition"
        >
          {autoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
          {autoBusy ? "Composing…" : "Compose for me"}
        </button>
      </div>

      <div className="lg:col-span-3 space-y-3" data-testid="podcast-output">
        {loading && (
          <div className="rounded-xl border border-brand-amber/40 bg-bg-surface p-8 text-center">
            <div className="font-mono text-xs text-brand-amber animate-pulse">// PRODUCING PACKAGE...</div>
            <div className="mt-3 text-ink-secondary text-sm">Generating {fields.length} assets</div>
          </div>
        )}
        {!loading && !result && (
          <div className="rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center text-ink-muted text-sm">
            Titles, descriptions, show notes & more land here.
          </div>
        )}
        {result?.episode && <CoverArtPanel episode={result.episode} topic={form.topic} />}
        {result?.fields && Object.entries(result.fields).map(([k, v]) => (
          <OutputBlock key={k} title={k.replace(/_/g, " ")} output={v} testid={`podcast-result-${k}`} />
        ))}
      </div>
    </div>
  );
}

function CoverArtPanel({ episode, topic }) {
  const [art, setArt] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/ai/generate-cover", {
        prompt: topic || episode.title,
        episode_id: episode.episode_id,
        style: "moody-cinematic",
      });
      setArt(res.data.image_data_url);
      toast.success("Cover art ready");
    } catch { toast.error("Cover art generation failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="rounded-xl border border-brand-amber/30 bg-bg-surface p-5" data-testid="cover-art-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono type-status-badge uppercase tracking-wider text-brand-amber">// COVER ART · NANO BANANA</div>
        {!art && (
          <button
            onClick={generate}
            disabled={loading}
            data-testid="generate-cover-btn"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-3 py-1.5 type-button-label text-black amber-glow disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {loading ? "Generating..." : "Generate cover"}
          </button>
        )}
      </div>
      {!art && !loading && (
        <div className="type-body text-ink-muted">
          Auto-generate a 1:1 square cover image for this episode using Gemini Nano Banana.
        </div>
      )}
      {loading && (
        <div className="aspect-square w-64 rounded-lg border border-line bg-bg flex items-center justify-center">
          <Loader2 className="h-6 w-6 text-brand-amber animate-spin" />
        </div>
      )}
      {art && (
        <div className="flex flex-col sm:flex-row gap-4">
          <img src={art} alt="cover" className="w-64 aspect-square rounded-lg border border-line object-cover" data-testid="cover-art-image" />
          <div className="space-y-2">
            <a
              href={art}
              download={`cover-${episode.episode_id}.png`}
              data-testid="cover-download"
              className="inline-flex items-center gap-2 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs hover:text-cyan-300 transition"
            >
              Download PNG
            </a>
            <button
              onClick={generate}
              disabled={loading}
              data-testid="cover-regenerate"
              className="block text-xs text-ink-muted hover:text-cyan-300"
            >
              ↻ Regenerate
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

